// Navigation Manager - Handles section navigation and routing
// Adapted from AgentGroupChat navigation patterns for React Router

import React from 'react';
import { logger } from '../core';

export class NavigationManager {
  constructor() {
    this.navigate = null;
    this.currentSection = 'main';
    this.sectionRoutes = {
      main: '/main',
      app: '/app',
      account: '/account',
      auth: '/auth',
      "accept-invite": '/accept-invite',
      pricing: '/pricing',
      marketplace: '/marketplace',
      checkout: '/checkout',
      assets: '/assets',
      "app-settings": '/app-settings',
      downloads: '/downloads',
      playspace: '/playspace',
      community: '/community',
      learn: '/learn',
      workspace: '/workspace',
      admin: '/admin'
    };

    // Unsafe change checker registry
    // Format: { sectionName: { checker: async function, message: string } }
    this.unsafeChangeCheckers = {};

    // Pending navigation (stored when blocked by unsafe changes)
    this.pendingNavigation = null;

    // Global unsafe changes state cache (updated periodically)
    this.unsafeChangesCache = {
      hasUnsafe: false,
      message: null,
      lastChecked: 0
    };

    logger.log({
      tags: "manager|navigation|init",
      color1: "cyan"
    }, "NavigationManager initialized");
  }

  /**
   * Set the navigate function from React Router
   * @param {Function} navigateFunction - useNavigate hook
   */
  setNavigateFunction(navigateFunction) {
    this.navigate = navigateFunction;
    logger.log({
      tags: "manager|navigation|ready",
      color1: "lime"
    }, "Navigation function set");
  }

  /**
   * Register an unsafe change checker for a section
   * @param {string} sectionName - Name of the section (e.g., 'workspace')
   * @param {Function} checkerFunction - Async function that returns Promise<boolean> indicating if there are unsafe changes
   * @param {string} message - Optional custom message to show when unsafe changes are detected
   */
  registerUnsafeChangeChecker(sectionName, checkerFunction, message = null) {
    if (typeof checkerFunction !== 'function') {
      logger.error({
        tags: "manager|navigation|error",
        color1: "red"
      }, `Invalid checker function for section ${sectionName}`);
      return;
    }

    this.unsafeChangeCheckers[sectionName] = {
      checker: checkerFunction,
      message: message || `You have unsaved changes in ${sectionName}. Are you sure you want to leave?`
    };

    logger.log({
      tags: "manager|navigation|unsafe",
      color1: "cyan"
    }, `Registered unsafe change checker for section: ${sectionName}`);
  }

  /**
   * Unregister an unsafe change checker for a section
   * @param {string} sectionName - Name of the section
   */
  unregisterUnsafeChangeChecker(sectionName) {
    if (this.unsafeChangeCheckers[sectionName]) {
      delete this.unsafeChangeCheckers[sectionName];
      logger.log({
        tags: "manager|navigation|unsafe",
        color1: "cyan"
      }, `Unregistered unsafe change checker for section: ${sectionName}`);
    }
  }

  /**
   * Derive section name from a URL path
   * @param {string} path - URL path
   * @returns {string} Section name
   */
  deriveSectionFromPath(path) {
    if (!path) return null;
    
    const normalizedPath = path.toLowerCase();
    if (normalizedPath.startsWith('/workspace')) return 'workspace';
    if (normalizedPath.startsWith('/playspace')) return 'playspace';
    if (normalizedPath.startsWith('/app-settings')) return 'app-settings';
    if (normalizedPath.startsWith('/app')) return 'app';
    if (normalizedPath.startsWith('/assets')) return 'assets';
    if (normalizedPath.startsWith('/downloads')) return 'downloads';
    if (normalizedPath.startsWith('/community')) return 'community';
    if (normalizedPath.startsWith('/learn')) return 'learn';
    if (normalizedPath.startsWith('/pricing')) return 'pricing';
    if (normalizedPath.startsWith('/marketplace')) return 'marketplace';
    if (normalizedPath.startsWith('/account')) return 'account';
    if (normalizedPath.startsWith('/admin')) return 'admin';
    if (normalizedPath === '/' || normalizedPath.startsWith('/main')) return 'main';
    
    return null;
  }

  /**
   * Check if there are unsafe changes in the current section
   * @param {string} targetPath - Optional target path to check if we're leaving the section
   * @returns {Promise<{hasUnsafe: boolean, message: string|null}>}
   */
  async checkUnsafeChanges(targetPath = null) {
    // Derive current section from URL path if available (more reliable than currentSection)
    // This prevents issues where currentSection might be overwritten by Header component
    let currentSection = this.currentSection;
    
    // Try to get section from current URL if we're in browser context
    if (typeof window !== 'undefined' && window.location) {
      const urlSection = this.deriveSectionFromPath(window.location.pathname);
      if (urlSection) {
        currentSection = urlSection;
      }
    }
    
    const checker = this.unsafeChangeCheckers[currentSection];

    // No checker registered for current section
    if (!checker) {
      return { hasUnsafe: false, message: null };
    }

    // Check if we're leaving the section (not internal navigation)
    if (targetPath) {
      const isLeavingSection = this.isLeavingSection(currentSection, targetPath);
      if (!isLeavingSection) {
        // Internal navigation within the same section - don't block
        return { hasUnsafe: false, message: null };
      }
    }

    try {
      const hasUnsafe = await checker.checker();
      return {
        hasUnsafe,
        message: hasUnsafe ? checker.message : null
      };
    } catch (error) {
      logger.error({
        tags: "manager|navigation|unsafe",
        color1: "red"
      }, `Error checking unsafe changes for ${currentSection}:`, error);
      // On error, assume unsafe to be safe
      return { hasUnsafe: true, message: checker.message };
    }
  }

  /**
   * Check if navigation target is leaving the current section
   * @param {string} sectionName - Current section name
   * @param {string} targetPath - Target path
   * @returns {boolean} True if leaving the section
   */
  isLeavingSection(sectionName, targetPath) {
    const sectionRoute = this.sectionRoutes[sectionName];
    if (!sectionRoute) {
      return true; // Unknown section, assume leaving
    }

    // Normalize paths for comparison (remove query params and hash)
    const normalizePath = (path) => {
      try {
        const url = new URL(path, window.location.origin);
        return url.pathname;
      } catch {
        // If URL parsing fails, just use the path as-is
        return path.split('?')[0].split('#')[0];
      }
    };

    const normalizedSectionRoute = normalizePath(sectionRoute);
    const normalizedTargetPath = normalizePath(targetPath);

    // Check if target path is still within the same section
    // For workspace: /workspace/:appId?tab=code is still in workspace
    // For workspace: /workspace/:appId -> /app-settings/:appId is leaving
    if (normalizedTargetPath.startsWith(normalizedSectionRoute)) {
      // Still in the same section (could be query params or sub-routes)
      return false;
    }

    return true;
  }

  /**
   * Navigate to a section by name (with unsafe change checking)
   * @param {string} sectionName - Name of the section to navigate to
   * @param {Object} options - Navigation options
   * @param {boolean} options.force - If true, skip unsafe change check
   * @param {Function} options.onComplete - Optional callback to execute after navigation succeeds
   */
  async navigateTo(sectionName, options = {}) {
    if (!this.navigate) {
      logger.error({
        tags: "manager|navigation|error",
        color1: "red",
        color2: "orange",
        includeSource: true
      }, "Navigation function not set! Call setNavigateFunction first");
      return;
    }

    // Extract onComplete callback from options (don't pass it to React Router)
    const { onComplete, force, ...routerOptions } = options;

    // Try to get registered route, otherwise construct from section name
    const route = this.sectionRoutes[sectionName] || `/${sectionName}`;

    // Check for unsafe changes (unless forced)
    if (!force) {
      const unsafeCheck = await this.checkUnsafeChanges(route);
      if (unsafeCheck.hasUnsafe) {
        // Store pending navigation and trigger confirmation modal
        // Include onComplete callback so it can be executed after user confirms
        this.pendingNavigation = {
          type: 'section',
          target: sectionName,
          route,
          options: routerOptions,
          onComplete: onComplete // Store callback for later execution
        };
        
        // Trigger custom event for confirmation modal
        window.dispatchEvent(new CustomEvent('navigation:unsafe-changes', {
          detail: {
            hasUnsafe: true,
            message: unsafeCheck.message,
            pendingNavigation: this.pendingNavigation
          }
        }));

        return false; // Navigation blocked
      }
    }

    logger.log({
      tags: "manager|navigation|action",
      color: "cyan"
    }, `Navigating to section: ${sectionName} (${route})`);

    this.currentSection = sectionName;
    this.navigate(route, routerOptions);
    
    // Execute onComplete callback if provided (navigation succeeded immediately)
    if (onComplete && typeof onComplete === 'function') {
      try {
        await onComplete();
      } catch (error) {
        logger.error({
          tags: "manager|navigation|error",
          color1: "red"
        }, "Error executing onComplete callback:", error);
      }
    }
    
    return true; // Navigation successful
  }

  /**
   * Execute pending navigation (called after user confirms)
   */
  async executePendingNavigation() {
    if (!this.pendingNavigation) {
      return;
    }

    const pending = this.pendingNavigation;
    this.pendingNavigation = null;

    // Extract onComplete callback before executing navigation
    const { onComplete } = pending;

    if (pending.type === 'section') {
      await this.navigateTo(pending.target, { ...pending.options, force: true });
    } else if (pending.type === 'path') {
      await this.navigateToPath(pending.target, { ...pending.options, force: true });
    } else if (pending.type === 'back') {
      if (this.navigate) {
        this.navigate(-1);
      }
    }

    // Execute onComplete callback after navigation succeeds (if provided)
    if (onComplete && typeof onComplete === 'function') {
      try {
        await onComplete();
      } catch (error) {
        logger.error({
          tags: "manager|navigation|error",
          color1: "red"
        }, "Error executing onComplete callback after pending navigation:", error);
      }
    }
  }

  /**
   * Cancel pending navigation (called when user cancels)
   */
  cancelPendingNavigation() {
    this.pendingNavigation = null;
  }

  /**
   * Navigate to an explicit path (supports dynamic params, with unsafe change checking)
   * @param {string} path - Full path to navigate to (e.g., /playspace/123)
   * @param {Object} options - Navigation options
   * @param {boolean} options.force - If true, skip unsafe change check
   */
  async navigateToPath(path, options = {}) {
    if (!this.navigate) {
      logger.error({
        tags: "manager|navigation|error",
        color1: "red",
        color2: "orange",
        includeSource: true
      }, "Navigation function not set! Call setNavigateFunction first");
      return;
    }

    // Check for unsafe changes (unless forced)
    if (!options.force) {
      const unsafeCheck = await this.checkUnsafeChanges(path);
      if (unsafeCheck.hasUnsafe) {
        // Store pending navigation and trigger confirmation modal
        this.pendingNavigation = {
          type: 'path',
          target: path,
          options
        };
        
        // Trigger custom event for confirmation modal
        window.dispatchEvent(new CustomEvent('navigation:unsafe-changes', {
          detail: {
            hasUnsafe: true,
            message: unsafeCheck.message,
            pendingNavigation: this.pendingNavigation
          }
        }));

        return false; // Navigation blocked
      }
    }

    logger.log({
      tags: "manager|navigation|action",
      color: "cyan"
    }, `Navigating to path: ${path}`);

    // Update currentSection for payload system compatibility
    const targetSection = this.deriveSectionFromPath(path);
    if (targetSection) {
      const oldSection = this.currentSection;
      this.currentSection = targetSection;
      logger.log({
        tags: "manager|navigation|section",
        color: "blue"
      }, `Updated currentSection: ${oldSection} → ${targetSection} for path: ${path}`);
    } else {
      logger.log({
        tags: "manager|navigation|section|error",
        color: "yellow"
      }, `No section found for path: ${path}, currentSection remains: ${this.currentSection}`);
    }

    this.navigate(path, options);
    return true; // Navigation successful
  }

  /**
   * Navigate to the main/home section
   * @param {Object} options - Navigation options
   * @param {Function} options.onComplete - Optional callback to execute after navigation succeeds
   * @returns {Promise<boolean>} True if navigation succeeded, false if blocked
   */
  async goHome(options = {}) {
    // Navigate to main section (which will be at /main)
    // Pass onComplete callback through to navigateTo
    return await this.navigateTo('main', options);
  }


  /**
   * Go back in navigation history (with unsafe change checking)
   */
  async goBack() {
    if (this.navigate) {
      // Note: We can't check target path for browser back, so we check current section
      const unsafeCheck = await this.checkUnsafeChanges();
      if (unsafeCheck.hasUnsafe) {
        // Store pending navigation
        this.pendingNavigation = {
          type: 'back',
          target: -1
        };
        
        // Trigger custom event for confirmation modal
        window.dispatchEvent(new CustomEvent('navigation:unsafe-changes', {
          detail: {
            hasUnsafe: true,
            message: unsafeCheck.message,
            pendingNavigation: this.pendingNavigation
          }
        }));

        return false; // Navigation blocked
      }

      logger.log({
        tags: "manager|navigation|action",
        color: "cyan"
      }, "Going back in navigation");
      this.navigate(-1);
      return true;
    }
    return false;
  }

  /**
   * Go forward in navigation history
   */
  goForward() {
    if (this.navigate) {
      logger.log({
        tags: "manager|navigation|action",
        color: "cyan"
      }, "Going forward in navigation");
      this.navigate(1);
    }
  }

  /**
   * Get the current section name
   * @returns {string} Current section name
   */
  getCurrentSection() {
    return this.currentSection;
  }

  /**
   * Check for unsafe changes (for beforeunload event)
   * Uses cached value if recent, otherwise checks all registered checkers
   * @returns {Promise<{hasUnsafe: boolean, message: string|null}>}
   */
  async checkUnsafeChangesForBeforeUnload() {
    // Use cached value if checked recently (within last 500ms)
    const now = Date.now();
    if (this.unsafeChangesCache.lastChecked && (now - this.unsafeChangesCache.lastChecked) < 500) {
      return {
        hasUnsafe: this.unsafeChangesCache.hasUnsafe,
        message: this.unsafeChangesCache.message
      };
    }

    // Check current section (checkUnsafeChanges now derives section from URL, so it's reliable)
    const result = await this.checkUnsafeChanges();
    
    // Update cache
    this.unsafeChangesCache = {
      hasUnsafe: result.hasUnsafe,
      message: result.message,
      lastChecked: now
    };

    return result;
  }

  /**
   * Check if a section exists
   * @param {string} sectionName - Name of section to check
   * @returns {boolean} True if section exists
   */
  hasSection(sectionName) {
    return sectionName in this.sectionRoutes;
  }

  /**
   * Get the route path for a section
   * @param {string} sectionName - Name of the section
   * @returns {string|null} Route path or null if not found
   */
  getRouteForSection(sectionName) {
    return this.sectionRoutes[sectionName] || null;
  }

  /**
   * Add a new section route (for dynamic sections)
   * @param {string} sectionName - Name of the new section
   * @param {string} route - Route path
   */
  addSection(sectionName, route) {
    this.sectionRoutes[sectionName] = route;
    logger.log({
      tags: "manager|navigation|update",
      color: "yellow"
    }, `Added new section: ${sectionName} -> ${route}`);
  }

  async navigateToAppSettings(appId, options = {}) {
    if (!this.navigate) {
      logger.error({
        tags: "manager|navigation|error",
        color1: "red",
        color2: "orange",
        includeSource: true
      }, "Navigation function not set! Call setNavigateFunction first");
      return;
    }
    const route = `/app-settings/${appId}`;
    
    // Check for unsafe changes (unless forced)
    if (!options.force) {
      const unsafeCheck = await this.checkUnsafeChanges(route);
      if (unsafeCheck.hasUnsafe) {
        // Store pending navigation and trigger confirmation modal
        this.pendingNavigation = {
          type: 'path',
          target: route,
          options
        };
        
        // Trigger custom event for confirmation modal
        window.dispatchEvent(new CustomEvent('navigation:unsafe-changes', {
          detail: {
            hasUnsafe: true,
            message: unsafeCheck.message,
            pendingNavigation: this.pendingNavigation
          }
        }));

        return false; // Navigation blocked
      }
    }

    this.currentSection = 'app-settings';
    this.navigate(route, options);
    return true; // Navigation successful
  }

  /**
   * Remove a section route
   * @param {string} sectionName - Name of the section to remove
   */
  removeSection(sectionName) {
    if (this.sectionRoutes[sectionName]) {
      delete this.sectionRoutes[sectionName];
      logger.log({
        tags: "manager|navigation|update",
        color: "yellow"
      }, `Removed section: ${sectionName}`);
    }
  }

  /**
   * Navigate to a section with a payload - general purpose section-to-section payload system
   * @param {string} targetSection - Section to navigate to
   * @param {Object} payload - Data payload to deliver to target section
   * @param {Object} options - Additional navigation options
   */
  async navigateWithPayload(targetSection, payload = {}, options = {}) {
    // Store payload for the target section
    const payloadData = {
      payload,
      timestamp: Date.now(),
      sourceSection: this.currentSection
    };

    sessionStorage.setItem(`sectionPayload_${targetSection}`, JSON.stringify(payloadData));

    logger.log({
      tags: "manager|navigation|payload",
      color: "yellow"
    }, `Navigating to ${targetSection} with payload`);

    // Navigate to target section
    await this.navigateTo(targetSection, options);
  }

  /**
   * Get payload for current section (if any)
   * @returns {Object|null} Payload data or null if none
   */
  getSectionPayload() {
    try {
      const sectionKey = `sectionPayload_${this.currentSection}`;
      const data = sessionStorage.getItem(sectionKey);

      if (data) {
        const parsed = JSON.parse(data);

        // Optional: check if data is not too old (e.g., 1 hour)
        const oneHour = 60 * 60 * 1000;
        if (Date.now() - parsed.timestamp > oneHour) {
          this.clearSectionPayload();
          return null;
        }

        return parsed.payload;
      }
    } catch (error) {
      logger.error({
        tags: "manager|navigation|error",
        color: "red"
      }, "Failed to parse section payload:", error);
      this.clearSectionPayload();
    }
    return null;
  }

  /**
   * Clear payload for current section
   */
  clearSectionPayload() {
    const sectionKey = `sectionPayload_${this.currentSection}`;
    sessionStorage.removeItem(sectionKey);
  }

  /**
   * Set a named payload that can be retrieved by any section
   * @param {string} key - Unique identifier for the payload
   * @param {Object} data - The payload data object
   */
  setPayload(key, data) {
    if (typeof data !== 'object' || data === null) {
      logger.error({
        tags: "manager|navigation|payload|error",
        color1: "red"
      }, `Invalid payload data for key "${key}": must be an object`);
      return;
    }

    const payloadData = {
      data,
      timestamp: Date.now(),
      sourceSection: this.currentSection
    };

    sessionStorage.setItem(`payload_${key}`, JSON.stringify(payloadData));

    logger.log({
      tags: "manager|navigation|payload",
      color1: "cyan"
    }, `Set payload "${key}" from section: ${this.currentSection}`);
  }

  /**
   * Get a named payload by key
   * @param {string} key - The payload key
   * @returns {Object|null} The payload data or null if not found/expired
   */
  getPayload(key) {
    try {
      const data = sessionStorage.getItem(`payload_${key}`);
      if (!data) return null;

      const parsed = JSON.parse(data);

      // Check if data is not too old (1 hour)
      const oneHour = 60 * 60 * 1000;
      if (Date.now() - parsed.timestamp > oneHour) {
        this.clearPayload(key);
        return null;
      }

      return parsed.data;
    } catch (error) {
      logger.error({
        tags: "manager|navigation|payload|error",
        color1: "red"
      }, `Failed to parse payload "${key}":`, error);
      this.clearPayload(key);
      return null;
    }
  }

  /**
   * Get and consume (remove) a named payload in one operation
   * @param {string} key - The payload key
   * @returns {Object|null} The payload data or null if not found/expired
   */
  consumePayload(key) {
    const payload = this.getPayload(key);
    if (payload !== null) {
      this.clearPayload(key);
      logger.log({
        tags: "manager|navigation|payload",
        color1: "green"
      }, `Consumed payload "${key}"`);
    }
    return payload;
  }

  /**
   * Clear a named payload
   * @param {string} key - The payload key to remove
   */
  clearPayload(key) {
    sessionStorage.removeItem(`payload_${key}`);
    logger.log({
      tags: "manager|navigation|payload",
      color1: "gray"
    }, `Cleared payload "${key}"`);
  }

  /**
   * Check if a named payload exists and is not expired
   * @param {string} key - The payload key
   * @returns {boolean} True if payload exists and is valid
   */
  hasPayload(key) {
    return this.getPayload(key) !== null;
  }

  /**
   * Require authentication - navigate to auth section with return URL and payload
   * @param {string} returnUrl - URL to return to after login
   * @param {Object} payload - Data payload to preserve across login
   */
  async requireAuth(returnUrl, payload = {}) {
    // Use the general payload system, but store auth-specific data
    const authPayload = {
      returnUrl,
      payload,
      timestamp: Date.now()
    };

    sessionStorage.setItem('authReturnData', JSON.stringify(authPayload));

    logger.log({
      tags: "manager|navigation|auth",
      color: "yellow"
    }, `Requiring auth, return to: ${returnUrl}`);

    // Navigate to auth section
    await this.navigateTo('auth');
  }

  /**
   * Get stored authentication return data
   * @returns {Object|null} Return data with returnUrl and payload, or null if none
   */
  getAuthReturnData() {
    try {
      const data = sessionStorage.getItem('authReturnData');
      if (data) {
        const parsed = JSON.parse(data);
        // Optional: check if data is not too old (e.g., 1 hour)
        const oneHour = 60 * 60 * 1000;
        if (Date.now() - parsed.timestamp > oneHour) {
          this.clearAuthReturnData();
          return null;
        }
        return parsed;
      }
    } catch (error) {
      logger.error({
        tags: "manager|navigation|error",
        color: "red"
      }, "Failed to parse auth return data:", error);
      this.clearAuthReturnData();
    }
    return null;
  }

  /**
   * Clear stored authentication return data
   */
  clearAuthReturnData() {
    sessionStorage.removeItem('authReturnData');
  }

  /**
   * Complete authentication redirect - navigate back to stored return URL
   * @returns {boolean} True if redirect was performed, false if no return data
   */
  completeAuthRedirect() {
    const returnData = this.getAuthReturnData();
    if (returnData) {
      this.clearAuthReturnData();

      logger.log({
        tags: "manager|navigation|auth",
        color: "green"
      }, `Completing auth redirect to: ${returnData.returnUrl}`);

      // Navigate back to the return URL with state containing the payload
      this.navigate(returnData.returnUrl, {
        state: returnData.payload,
        replace: true // Replace current history entry
      });

      return true;
    }
    return false;
  }
}

// Create global navigation manager instance
export const navigationManager = new NavigationManager();

// React hook for using navigation manager
export function useNavigationManager() {
  return navigationManager;
}

// React hook for accessing section payloads (legacy - use usePayload for new key-based system)
export function useSectionPayload() {
  const [payload, setPayload] = React.useState(null);
  const [isInitialized, setIsInitialized] = React.useState(false);

  React.useEffect(() => {
    // Get payload for current section
    const sectionPayload = navigationManager.getSectionPayload();
    setPayload(sectionPayload);
    setIsInitialized(true);

    // Clear payload after component mounts (one-time use)
    if (sectionPayload) {
      // Use setTimeout to clear after render cycle
      setTimeout(() => {
        navigationManager.clearSectionPayload();
      }, 0);
    }
  }, []);

  return { payload, isInitialized };
}

// React hook for accessing key-based payloads
export function usePayload(key) {
  const [payload, setPayload] = React.useState(null);
  const [isInitialized, setIsInitialized] = React.useState(false);

  React.useEffect(() => {
    // Get payload by key
    const keyPayload = navigationManager.getPayload(key);
    setPayload(keyPayload);
    setIsInitialized(true);

    // Clear payload after component mounts (one-time use)
    if (keyPayload) {
      // Use setTimeout to clear after render cycle
      setTimeout(() => {
        navigationManager.clearPayload(key);
      }, 0);
    }
  }, [key]);

  return { payload, isInitialized };
}

// React hook for consuming key-based payloads manually
export function useConsumePayload(key) {
  return React.useCallback(() => {
    return navigationManager.consumePayload(key);
  }, [key]);
}
